import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Pencil, Building2, Star, ArrowDownRight, ArrowUpRight, Power, PowerOff } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { useBranchStore } from '@/stores';
import { PageInfoCallout } from '@/components/common/PageInfoCallout';
import { toast } from 'sonner';

export default function BankAccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const { branches, fetchBranches } = useBranchStore();
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '', bank_name: '', account_number: '', iban: '', branch_ids: [], is_default: false, opening_balance: 0, default_gl_code: '', notes: '', active_from: ''
  });

  const fetchAccounts = async () => {
    try {
      fetchBranches();
      const res = await api.get('/bank-accounts');
      setAccounts(res.data);
    } catch { toast.error('Failed to load bank accounts'); }
    finally { setLoading(false); }
  };

  const [bankGl, setBankGl] = useState([]);
  useEffect(() => {
    fetchAccounts();
    api.get('/accounting/accounts').then((r) => {
      const rows = Array.isArray(r.data) ? r.data : (r.data?.accounts || []);
      setBankGl(rows.filter((a) => /^012-/.test(a.code || '') && !/cash|petty|gateway/i.test(a.name || '') && a.is_active !== false));
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const ids = (formData.branch_ids || []).filter(Boolean);
      const payload = {
        ...formData,
        branch_ids: ids,
        branch_id: ids[0] || null,
      };
      if (editingId) {
        await api.put(`/bank-accounts/${editingId}`, payload);
        toast.success('Bank account updated');
      } else {
        await api.post('/bank-accounts', payload);
        toast.success('Bank account added');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', bank_name: '', account_number: '', iban: '', branch_ids: [], is_default: false, opening_balance: 0, default_gl_code: '', notes: '', active_from: '' });
      fetchAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleEdit = (acc) => {
    const ids = (acc.branch_ids && acc.branch_ids.length)
      ? acc.branch_ids
      : (acc.branch_id ? [acc.branch_id] : []);
    setFormData({
      name: acc.name, bank_name: acc.bank_name, account_number: acc.account_number,
      iban: acc.iban || '', branch_ids: ids, is_default: acc.is_default || false, opening_balance: acc.opening_balance || 0, default_gl_code: acc.default_gl_code || '', notes: acc.notes || '', active_from: acc.active_from || ''
    });
    setEditingId(acc.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this bank account?')) return;
    try {
      await api.delete(`/bank-accounts/${id}`);
      toast.success('Deleted');
      fetchAccounts();
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = (d && typeof d === 'object') ? d.message : (d || 'Failed to delete');
      if (err.response?.status === 409) {
        // Dependencies exist — offer deactivate instead of delete.
        if (window.confirm(`${msg}\n\nDeactivate this account now? It stays in all history & reports but won't appear for new transactions.`)) {
          await handleSetStatus(id, false);
        }
      } else {
        toast.error(msg);
      }
    }
  };

  const handleSetStatus = async (id, active) => {
    try {
      await api.put(`/bank-accounts/${id}/status`, { active });
      toast.success(active ? 'Account reactivated' : 'Account deactivated');
      fetchAccounts();
    } catch { toast.error('Failed to update status'); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* What is this page? — collapsible orientation help. */}
        <PageInfoCallout
          storageKey="bank-accounts"
          title="What is the Bank Accounts module?"
        >
          <p>
            Register every bank account your business uses (current accounts, savings, USD/EUR holdings, etc.) so the rest of the app can attribute payments correctly. Each account shows its <strong>% reconciled</strong> against your latest statement, opening/closing balances, and per-branch ownership. Mark one as <strong>default</strong> to auto-select it in the bank-payment dialog.
          </p>
        </PageInfoCallout>

        <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg shadow-sky-200/50">
              <Building2 size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Bank Accounts</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Manage your company bank accounts for payment tracking</p>
            </div>
          </div>
          <Button className="rounded-xl" onClick={() => { setEditingId(null); setFormData({ name: '', bank_name: '', account_number: '', iban: '', branch_ids: [], is_default: false, opening_balance: 0, default_gl_code: '', notes: '', active_from: '' }); setShowForm(true); }}
            data-testid="add-bank-account-btn">
            <Plus size={16} className="mr-1" /> Add Bank Account
          </Button>
        </div>

        {/* Bank Account Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map(acc => (
            <Card key={acc.id} className={`border transition-all hover:shadow-md ${acc.is_default ? 'border-success/30 bg-success/10' : 'border-border'}`}
              data-testid={`bank-account-card-${acc.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl bg-info/20 flex items-center justify-center">
                      <Building2 size={20} className="text-info" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm flex items-center gap-1.5">
                        {acc.name}
                        {acc.is_default && <Star size={12} className="text-warning fill-amber-500" />}
                        {acc.is_active === false && (
                          <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500" data-testid={`bank-inactive-badge-${acc.id}`}>Inactive</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{acc.bank_name}</div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(acc)} data-testid="edit-bank-btn"><Pencil size={12} /></Button>
                    {acc.is_active === false ? (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600" title="Reactivate" onClick={() => handleSetStatus(acc.id, true)} data-testid={`reactivate-bank-btn-${acc.id}`}><Power size={12} /></Button>
                    ) : (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-600" title="Deactivate" onClick={() => handleSetStatus(acc.id, false)} data-testid={`deactivate-bank-btn-${acc.id}`}><PowerOff size={12} /></Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-error" onClick={() => handleDelete(acc.id)} data-testid="delete-bank-btn"><Trash2 size={12} /></Button>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Account #</span>
                    <span className="font-mono font-medium">{acc.account_number}</span>
                  </div>
                  {acc.iban && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">IBAN</span>
                      <span className="font-mono text-xs">{acc.iban}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Branches</span>
                    {(() => {
                      const ids = (acc.branch_ids && acc.branch_ids.length) ? acc.branch_ids : (acc.branch_id ? [acc.branch_id] : []);
                      if (ids.length === 0) return <span className="text-xs text-muted-foreground">All Branches</span>;
                      const names = ids.map(id => branches.find(b => b.id === id)?.name).filter(Boolean);
                      return (
                        <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                          {names.slice(0, 3).map(n => <Badge key={n} variant="outline" className="text-[10px]">{n}</Badge>)}
                          {names.length > 3 && <Badge variant="outline" className="text-[10px]">+{names.length - 3}</Badge>}
                        </div>
                      );
                    })()}
                  </div>
                  {acc.is_default && <Badge className="bg-success/20 text-success text-[10px]">Default Account</Badge>}
                </div>
                <div className="mt-3 pt-3 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Current Balance</span>
                    <span className={`text-lg font-bold ${(acc.current_balance || 0) >= 0 ? 'text-success' : 'text-error'}`} data-testid={`bank-balance-${acc.id}`}>
                      SR {(acc.current_balance || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-2 mt-1.5">
                    <span className="text-[10px] text-success/80 flex items-center gap-1" data-testid={`bank-incoming-${acc.id}`}>
                      <ArrowDownRight size={10} /> Incoming
                    </span>
                    <span className="text-[11px] font-semibold text-success">
                      SR {(acc.total_incoming || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-error/80 flex items-center gap-1" data-testid={`bank-outgoing-${acc.id}`}>
                      <ArrowUpRight size={10} /> Outgoing
                    </span>
                    <span className="text-[11px] font-semibold text-error">
                      SR {(acc.total_outgoing || 0).toLocaleString()}
                    </span>
                  </div>
                  {/* Reconciliation split — shows what % of txns on this
                      account have been matched to a sale/expense/payment.
                      Always rendered so users can see the indicator at a
                      glance; falls back to a dim "No statements yet" hint
                      when no statements have been uploaded. */}
                  {acc.reconciliation && acc.reconciliation.total_txns > 0 ? (() => {
                    const r = acc.reconciliation;
                    const tier = r.match_pct >= 90 ? 'emerald' : r.match_pct >= 60 ? 'amber' : 'rose';
                    return (
                      <div className="mt-2 pt-2 border-t border-stone-200 dark:border-stone-800" data-testid={`bank-recon-${acc.id}`}>
                        <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                          <span>Reconciled</span>
                          <span className={`font-bold text-${tier}-600 dark:text-${tier}-400 tabular-nums`}>
                            {r.matched_txns}/{r.total_txns} · {r.match_pct.toFixed(0)}%
                          </span>
                        </div>
                        <div className="h-1.5 bg-stone-200 dark:bg-stone-800 rounded-full overflow-hidden flex">
                          <div
                            className={`h-full bg-${tier}-500 transition-all`}
                            style={{ width: `${r.match_pct}%` }}
                          />
                        </div>
                        {r.unmatched_txns > 0 && (
                          <div className="text-[9px] text-muted-foreground mt-1">
                            {r.unmatched_txns} unmatched · review on Bank Statements page
                          </div>
                        )}
                      </div>
                    );
                  })() : (
                    <div className="mt-2 pt-2 border-t border-stone-200 dark:border-stone-800" data-testid={`bank-recon-empty-${acc.id}`}>
                      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                        <span>Reconciled</span>
                        <span className="font-medium text-muted-foreground/70 tabular-nums">— · no statements</span>
                      </div>
                      <div className="h-1.5 bg-stone-200/60 dark:bg-stone-800/60 rounded-full mt-1" />
                      <Link
                        to={`/bank-statements?account=${acc.id}`}
                        data-testid={`bank-recon-empty-cta-${acc.id}`}
                        className="block text-[9px] text-primary hover:underline mt-1 font-medium"
                      >
                        Upload a bank statement to enable reconciliation →
                      </Link>
                    </div>
                  )}
                </div>
                {acc.notes && <p className="text-xs text-muted-foreground mt-2 italic">{acc.notes}</p>}
              </CardContent>
            </Card>
          ))}
          {accounts.length === 0 && (
            <Card className="md:col-span-3 border-dashed">
              <CardContent className="p-8 text-center">
                <Building2 size={40} className="mx-auto text-muted-foreground mb-3 opacity-30" />
                <p className="text-muted-foreground text-sm">No bank accounts added yet</p>
                <p className="text-xs text-muted-foreground mt-1">Add your company bank accounts to track payments</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent data-testid="bank-account-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit">{editingId ? 'Edit' : 'Add'} Bank Account</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Display Name *</Label>
                  <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Al Rajhi - Main" required data-testid="bank-name-input" />
                </div>
                <div>
                  <Label>Bank Name *</Label>
                  <Input value={formData.bank_name} onChange={e => setFormData({ ...formData, bank_name: e.target.value })} placeholder="e.g. Al Rajhi Bank" required data-testid="bank-bank-name-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Account Number *</Label>
                  <Input value={formData.account_number} onChange={e => setFormData({ ...formData, account_number: e.target.value })} placeholder="Account number" required data-testid="bank-account-number-input" />
                </div>
                <div>
                  <Label>IBAN</Label>
                  <Input value={formData.iban} onChange={e => setFormData({ ...formData, iban: e.target.value })} placeholder="SA..." data-testid="bank-iban-input" />
                </div>
              </div>
              <div>
                <Label>Linked Branches (one bank account can serve multiple branches)</Label>
                <div className="mt-2 flex flex-wrap gap-2 p-2 rounded-lg border border-border bg-muted/50 min-h-[44px]">
                  <button type="button"
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                      formData.branch_ids.length === 0
                        ? 'bg-info text-white border-info'
                        : 'bg-white text-muted-foreground border-border hover:border-info/30'
                    }`}
                    onClick={() => setFormData({ ...formData, branch_ids: [] })}
                    data-testid="bank-branch-all-btn"
                  >
                    All Branches
                  </button>
                  {branches.map(b => {
                    const active = formData.branch_ids.includes(b.id);
                    return (
                      <button key={b.id} type="button"
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                          active
                            ? 'bg-success text-white border-success'
                            : 'bg-white text-muted-foreground border-border hover:border-success/30'
                        }`}
                        onClick={() => {
                          const current = formData.branch_ids || [];
                          setFormData({
                            ...formData,
                            branch_ids: active ? current.filter(x => x !== b.id) : [...current, b.id],
                          });
                        }}
                        data-testid={`bank-branch-chip-${b.id}`}
                      >
                        {active ? '✓ ' : ''}{b.name}
                      </button>
                    );
                  })}
                </div>
                {/* iter922 — Date-based bank switching: this account serves the
                    linked branches for transactions ON/AFTER this date. Leave
                    empty for "always active". The account with the latest
                    Active-From ≤ the transaction date wins. */}
                <div className="mt-2">
                  <Label>Active From (bank switching)</Label>
                  <Input
                    type="date"
                    value={formData.active_from || ''}
                    onChange={e => setFormData({ ...formData, active_from: e.target.value })}
                    className="max-w-[220px]"
                    data-testid="bank-active-from-input"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Reports attribute each branch transaction to the bank that was active on that date — e.g. keep Alinma (no date) and add Al Rajhi with Active From 15 Aug to switch banks from Aug 15 onwards.
                  </p>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {formData.branch_ids.length === 0
                    ? 'Account will be shared with all branches.'
                    : `Selected ${formData.branch_ids.length} branch${formData.branch_ids.length !== 1 ? 'es' : ''}.`}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Opening Balance (SR)</Label>
                  <Input type="number" step="0.01" value={formData.opening_balance} onChange={e => setFormData({ ...formData, opening_balance: parseFloat(e.target.value) || 0 })} placeholder="0.00" data-testid="bank-opening-balance-input" />
                </div>
                {/* iter884 — GL account code for the cash leg of auto-booked
                    journal entries. Multi-bank tenants route each statement
                    to the correct GL account. Defaults to "1010" server-side. */}
                <div>
                  <Label>Ledger (GL) account</Label>
                  <select className="w-full h-9 rounded-md border bg-background px-2 text-sm" value={formData.default_gl_code || ''}
                    onChange={e => setFormData({ ...formData, default_gl_code: e.target.value })} data-testid="bank-default-gl-code-input">
                    <option value="">012-2 Bank Account (Main) — default</option>
                    {bankGl.filter((a) => a.code !== '012-2').map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
                    {formData.default_gl_code && !bankGl.some((a) => a.code === formData.default_gl_code) && <option value={formData.default_gl_code}>{formData.default_gl_code} (not in chart)</option>}
                  </select>
                  <p className="text-[11px] text-muted-foreground mt-1">New payments from this bank post here. Past journals are not moved. Add bank sub-accounts (012-x) in Chart of Accounts.</p>
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Optional notes" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={formData.is_default} onCheckedChange={v => setFormData({ ...formData, is_default: v })} id="is-default" />
                <Label htmlFor="is-default" className="cursor-pointer text-sm">Set as default bank account</Label>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-bank-btn">{editingId ? 'Update' : 'Add'} Bank Account</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
